import React from "react"
import PropTypes from "prop-types"
import Head from "next/head"
import DOMPurify from "isomorphic-dompurify"
import { EMPTY_PIXEL, FAQ_PAGE_DATA_URL } from "@/constants"
import { getProductStatus } from "@/utils/helper"
import { isObjectEmpty } from "@/utils/index"

// --- HELPER FUNCTIONS ---

const extractSelectedSku = (skuDetails = [], targetSkuId = "") => {
  const matchingDetail =
    skuDetails.find(item => item.includes(targetSkuId)) ?? ""
  const skuParts = matchingDetail.split("|")
  return skuParts[skuParts.length - 1] ?? ""
}

const getSkuWithoutColorFinish = (skuId = "") => {
  const lastDashIndex = skuId.lastIndexOf("-")
  return lastDashIndex !== -1 ? skuId.substring(0, lastDashIndex) : skuId
}

const getVariantImage = (variantImages = [], tmpSkuId = "", variants = []) => {
  if (!variantImages.length) return ""
  const selVarImageUrls = []
  const fallbackUrls = []

  variantImages.forEach(img => {
    const imgDetail = img.split("|")
    const imgSku = imgDetail[0]
    const url = imgDetail[imgDetail.length - 1] || EMPTY_PIXEL

    if (tmpSkuId === imgSku) {
      selVarImageUrls.push(url)
    } else if (!variants.some(v => v?.sku_s === imgSku)) {
      fallbackUrls.push(url)
    }
  })

  const mergedUrls = [...selVarImageUrls, ...fallbackUrls]
  const targetUrl = mergedUrls[0] || EMPTY_PIXEL
  const assetId = targetUrl.split("PAWEB/")[1] || ""

  return mergedUrls.find(image => image.includes(assetId)) ?? ""
}

const getAllVariantImages = ({
  variantImgEntries = [],
  tmpSkuId = "",
  variants = [],
  fallbackInitialUrl = "",
}) => {
  const primaryImage =
    getVariantImage(variantImgEntries, tmpSkuId, variants) || fallbackInitialUrl

  if (!variantImgEntries.length) {
    return primaryImage ? [primaryImage] : []
  }

  const selVarImageUrls = []
  const fallbackUrls = []

  variantImgEntries.forEach(img => {
    const imgDetail = img.split("|")
    const url = imgDetail[imgDetail.length - 1]
    if (!url || url === EMPTY_PIXEL) return

    if (tmpSkuId === imgDetail[0]) {
      selVarImageUrls.push(url)
    } else if (!variants.some(v => v?.sku_s === imgDetail[0])) {
      fallbackUrls.push(url)
    }
  })

  const uniqueUrls = Array.from(new Set([...selVarImageUrls, ...fallbackUrls]))
  if (!primaryImage) return uniqueUrls

  return [primaryImage, ...uniqueUrls.filter(url => url !== primaryImage)]
}

const formatReviewData = fullReviewData => {
  if (
    !fullReviewData ||
    !Array.isArray(fullReviewData) ||
    fullReviewData.length === 0
  ) {
    return {}
  }
  return fullReviewData.map(review => ({
    "@type": "Review",
    author: {
      "@type": "Person",
      name: review?.UserNickname || "Anonymous",
    },
    datePublished: review?.LastModificationTime,
    headline: review?.Title,
    reviewBody: review?.ReviewText,
    reviewRating: {
      "@type": "Rating",
      ratingValue: review?.Rating,
      bestRating: review?.RatingRange,
    },
  }))
}

const formatReviewStatsData = (reviewStatsData, productName) => {
  if (!reviewStatsData || Object.keys(reviewStatsData).length === 0) {
    return {}
  }
  return {
    "@type": "AggregateRating",
    itemReviewed: {
      "@type": "IndividualProduct",
      name: productName,
    },
    ratingValue: reviewStatsData?.AverageOverallRating,
    reviewCount: reviewStatsData?.TotalReviewCount,
    bestRating: reviewStatsData?.OverallRatingRange,
  }
}

// --- MAIN COMPONENT ---

const NextHead = props => {
  const {
    fullUrl = "",
    psKey = "",
    psCountry = "",
    psLanguage = "",
    metaLanguage = "",
    pdpData = {},
    reviewStatsData = {},
    fullReviewData = {},
    initialImageFullUrl = "",
    isPart = false,
    skuId = "",
    siteName = "",
    isBundleProduct = false,
    persona = "GST",
    config = {},
    isProductExcludeFromPaidAdvertising = false,
    variantImages = [],
    pdpShippingDetails = {},
  } = props

  const {
    ProductBrandName_s: productName = "",
    productName_s: partsName = "",
    "Color.SKU.Details_ss": skuDetails = [],
    productIsInStock_b: available = true,
    masterVariantDataResponse: variants = [],
    productName_s: productFullName = "",
    metaDescription_s: productMetaDescription = "",
    productRegionAllVariantDiscontinued_b: allVariantDiscontinued = false,
    RegionOnBackOrder_s: backorder = "",
    BundleDescription_s: bundleName = "",
    BundleSku_s: BundleSku = "",
  } = pdpData

  const { internationalization: { currencyCode = "USD" } = {} } = config
  const normalizedCountry = psCountry?.toUpperCase()
  const isUSWebsite = normalizedCountry === "US"

  const { shippingDetails: shippingDetailsArray = [] } =
    pdpShippingDetails || {}

  const merchantReturnPolicy = {
    "@type": "MerchantReturnPolicy",
    applicableCountry: normalizedCountry,
    returnPolicyCountry: normalizedCountry,
    returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
    merchantReturnDays: 90,
    returnMethod: "https://schema.org/ReturnByMail",
    returnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
    refundType: "https://schema.org/FullRefund",
    merchantReturnLink: "https://www.kohler.com/en/help/return-policy",
  }
  const isMultiFinishBundle = !!BundleSku
  const selectedSku = extractSelectedSku(skuDetails, skuId)
  const sku = isBundleProduct ? skuId : selectedSku

  const resolvedProductName =
    isPart || isBundleProduct
      ? partsName
      : isMultiFinishBundle
      ? bundleName
      : productFullName

  const rawPrice = pdpData?.[`priceList.${persona}.finalPrice_d`]
  const productPrice = rawPrice ? parseFloat(rawPrice).toFixed(2) : 0

  const getShippingDetails = (details = [], sku = "") => {
    const schemaData = details.find(v => v.sku === sku) || {}
    if (isObjectEmpty(schemaData)) return {}

    const {
      deliveryTime: { transitTime = {} } = {},
      shippingDestination = {},
      shippingRate = {},
    } = schemaData || {}

    return {
      "@type": "OfferShippingDetails",
      shippingRate: {
        "@type": "MonetaryAmount",
        ...shippingRate,
      },
      shippingDestination: {
        "@type": "DefinedRegion",
        ...shippingDestination,
      },
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        handlingTime: {
          "@type": "QuantitativeValue",
          minValue: 0,
          maxValue: 1,
          unitCode: "DAY",
        },
        transitTime: {
          "@type": "QuantitativeValue",
          unitCode: "DAY",
          ...transitTime,
        },
      },
    }
  }
  // Build Structured Data Variants Schema
  const getVariantsSchema = () => {
    if (variants.length <= 1) return []

    return variants.map(variant => {
      const variantSku = extractSelectedSku(skuDetails, variant?.sku_s ?? "")
      const shippingDetails = getShippingDetails(
        shippingDetailsArray,
        variant?.sku_s ?? ""
      )
      return {
        "@type": "Product",
        sku: variantSku,
        gtin: variant?.SKUUPCCode_s ?? "",
        image: getAllVariantImages({
          variantImgEntries: variant["productImages.labelWithUrl_ss"],
          tmpSkuId: variant?.sku_s,
          variants,
          fallbackInitialUrl: initialImageFullUrl,
        }),
        name: variant?.productName_s ?? "",
        description: variant?.metaDescription_s ?? "",
        color: variant?.ColorFinish_s ?? "",
        offers: {
          "@type": "Offer",
          url: `${fullUrl}?skuId=${variant?.sku_s ?? ""}`,
          priceCurrency: currencyCode,
          price: variant?.[`priceList.${persona}.finalPrice_d`] ?? 0,
          itemCondition: "https://schema.org/NewCondition",
          availability: variant?.productIsInStock_b
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
          ...(isUSWebsite
            ? { hasMerchantReturnPolicy: merchantReturnPolicy }
            : {}),
          ...(!isObjectEmpty(shippingDetails) ? { shippingDetails } : {}),
        },
      }
    })
  }

  // Base Structured Data Shared Props
  const masterVariant = variants.find(v => v.masterVariant_b)
  const commonSchemaProps = {
    "@context": "https://schema.org",
    "@id": fullUrl,
    url: fullUrl,
    additionalType: isPart ? "Service Part" : "",
    name: resolvedProductName,
    image: getAllVariantImages({
      variantImgEntries: variantImages,
      tmpSkuId: skuId,
      variants,
      fallbackInitialUrl: initialImageFullUrl,
    }),
    description: isBundleProduct ? partsName : productMetaDescription,
    gtin: masterVariant?.SKUUPCCode_s ?? "",
    brand: {
      "@type": "Brand",
      name: siteName,
    },
    aggregateRating: formatReviewStatsData(
      reviewStatsData,
      resolvedProductName
    ),
    review: formatReviewData(fullReviewData),
  }
  const productShippingDetails = getShippingDetails(shippingDetailsArray, skuId)
  // Product Group vs. Single Product Schema
  const productSchema =
    variants.length > 1
      ? [
          {
            ...commonSchemaProps,
            "@type": "ProductGroup",
            sku: getSkuWithoutColorFinish(sku),
            productGroupID: getSkuWithoutColorFinish(sku),
            variesBy: ["https://schema.org/color"],
            hasVariant: getVariantsSchema(),
          },
        ]
      : {
          ...commonSchemaProps,
          "@type": "Product",
          sku: sku,
          offers: {
            "@type": "Offer",
            price: rawPrice ?? 0,
            priceCurrency: currencyCode,
            availability: available
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
            itemCondition: "http://schema.org/NewCondition",
            ...(isUSWebsite
              ? { hasMerchantReturnPolicy: merchantReturnPolicy }
              : {}),
            ...(!isObjectEmpty(productShippingDetails)
              ? {
                  shippingDetails: productShippingDetails,
                }
              : {}),
          },
        }

  return (
    <Head>
      <meta name="keywords" content="Kohler US Plumbing Bathroom Kitchen" />
      {fullUrl.includes(FAQ_PAGE_DATA_URL) ? (
        <meta name="robots" content="noindex" />
      ) : null}
      {isProductExcludeFromPaidAdvertising ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : null}

      {productPrice ? (
        <>
          <meta property="og:price:amount" content={productPrice} />
          <meta property="og:price:currency" content={currencyCode || "USD"} />
        </>
      ) : null}

      <meta
        property="og:availability"
        content={getProductStatus(allVariantDiscontinued, backorder, available)}
      />

      {metaLanguage ? (
        <meta property="og:locale" content={metaLanguage.toLowerCase()} />
      ) : null}
      {fullUrl ? <link rel="canonical" href={fullUrl} /> : null}
      {psKey ? <meta name="ps-key" content={psKey} /> : null}
      {psCountry ? <meta name="ps-country" content={psCountry} /> : null}
      {psLanguage ? <meta name="ps-language" content={psLanguage} /> : null}

      {productName || partsName || bundleName ? (
        <script
          key="structured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(JSON.stringify(productSchema)),
          }}
        />
      ) : null}
    </Head>
  )
}

NextHead.propTypes = {
  reviewStatsData: PropTypes.object,
  fullReviewData: PropTypes.object,
  variantImages: PropTypes.array,
}

export default NextHead
